import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import NavigationLink from "../../components/NavigationLink/NavigationLink";
import { EditActions, editActions } from "../../actions/editActions";
import ContentLayout from "../../components/ContentLayout/ContentLayout";
import {
  isEditActiveSelector,
  editedObjectIdentifierSelector,
  editedObjectIdSelector,
  parentObjectIdSelector
} from "../../reducers/editReducer";
import { pdApiActions, PdApiActions } from "../../actions/pdApiActions";
import ActionMenu from "../common/ActionMenu/ActionMenu";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import { mainActions } from "../../actions/mainActions";
import Languages from "./Languages/Languages";
import Publish from "./Publish/Publish";
import Translation from "./Translation/Translation";
import { createEmptyLocale } from "../../utils/pdApiUtils";
import { isAdmin, hasRole,hasWriteAccess} from "../../utils/utils";
import TranslationStatus from "./TranslationStatus/TranslationStatus";
import TranslationLog from "./TranslationLog/TranslationLog";

type StateProps = {
  pdApi: State.PdApi;
  columns: State.ColumnMap;
  mainState: State.Main;
  editedObjectId: string;
  parentObjectId: string;
  editedObjectType: string;
  isEditActive: boolean;
  objectIdentifier: string | number | undefined;
  isLoadingStatistics: boolean;
  filterOptions: State.FilterOptions;
  translationStatusFilterOptions: State.TranslationStatusFilterOptions;
};

type DispatchProps = {
  pdApiActions: typeof pdApiActions;
  editActions: typeof editActions;
  mainActions: typeof mainActions;
};

type OwnProps = {
  subMenuRoute: State.LocalizationSubmenu;
  activeUser: Api.User | undefined;
};

// Props of the component
export type LocalizationProps = StateProps & DispatchProps & OwnProps;

type LocalizationState = {
  isModalOpen: boolean;
};

export class Localization extends React.Component<LocalizationProps, LocalizationState> {
  state = {
    isModalOpen: false
  };

  /**
   * To add new sub page to this parent, add it here
   */

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subPages: any = {
    Status: {
      dataView: TranslationStatus
    },
    "Translation Log": {
      dataView: TranslationLog
    },
    Languages: {
      itemName: "Language",
      PdApiField: "locales",
      dataView: Languages,
      createEmpty: createEmptyLocale
    },
    Publish: {
      dataView: Publish
    },
    Translation: {
      dataView: Translation
    }
  };

  render(): JSX.Element {
    const { subMenuRoute, activeUser } = this.props;
    const submenuLinks = (
      <>
        <NavigationLink
          key={"translationStatus"}
          name={"Status"}
          to={"/localization/translationStatus"}
        />
        <NavigationLink
          name={"Translation Log"}
          title={"Log"}
          to={"/localization/translationLog"}
        />
        {hasRole(activeUser, "translation") && (
          <NavigationLink
            key={"translation"}
            name={"Translation"}
            to={"/localization/translation"}
          />
        )}
        {hasWriteAccess(activeUser) && (
          <NavigationLink key={"publish"} name={"Publish"} to={"/localization/publish"} />
        )}

        {isAdmin(activeUser) && (
          <NavigationLink key={"languages"} name={"Languages"} to={"/localization/languages"} />
        )}
      </>
    );

    const actionMenu = this.getActionMenu(subMenuRoute);
    const dataView = this.getDataView(subMenuRoute);
    const modal = this.buildModal();

    return (
      <>
        <ContentLayout submenu={submenuLinks} actionMenu={actionMenu} dataView={dataView} />
        {modal}
      </>
    );
  }

  private getDataView = (subMenuRoute: State.LocalizationSubmenu): JSX.Element => {
    if (subMenuRoute in this.subPages) {
      return React.createElement(this.subPages[subMenuRoute].dataView, {
        ...this.props,
        toggleModal: this.toggleModal
      });
    }
    return <>Error</>;
  };

  private getActionMenu = (subMenuRoute: State.LocalizationSubmenu): JSX.Element | null => {
    if (
      subMenuRoute === "Status" ||
      subMenuRoute === "Translation Log" ||
      subMenuRoute === "Translation" ||
      subMenuRoute === "Publish" ||
      this.subPages[subMenuRoute].PdApiField === null
    ) {
      return null;
    }

    return (
      <ActionMenu
        {...this.props}
        itemName={this.subPages[subMenuRoute].itemName}
        selectedColumnsName={subMenuRoute}
        dataFieldName={this.subPages[subMenuRoute].PdApiField}
        setExtraProperties={this.subPages[subMenuRoute].setExtraProperties}
        createEmpty={this.subPages[subMenuRoute].createEmpty}
        showExport={subMenuRoute !== "Languages"}
        showTableSettings={subMenuRoute !== "Languages"}
      />
    );
  };

  private buildModal = () => {
    const content = (
      <span>
        Are you sure you want to delete this {this.props.editedObjectType}?
        <br />
        {this.props.objectIdentifier}
      </span>
    );

    return (
      <ConfirmationModal
        isOpen={this.state.isModalOpen}
        size="mini"
        header="Confirm delete"
        content={content}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={() => {
          this.props.editActions.delete("localizationids");
        }}
        onCancel={this.toggleModal}
      />
    );
  };

  /*
   * Toggles the modal based on previous state.
   */
  private toggleModal = (): void => {
    this.setState(prevState => ({ ...prevState, isModalOpen: !prevState.isModalOpen }));
  };
}
const mapStateToProps = (state: State.Root, ownProps: OwnProps) => ({
  columns: state.columns,
  pdApi: state.pdApi,
  mainState: state.main,
  isLoadingStatistics: state.loadingStatus.isLoadingStatistics,
  isEditActive: isEditActiveSelector(state.editView),
  editedObjectId: editedObjectIdSelector(state.editView),
  parentObjectId: parentObjectIdSelector(state.editView),
  editedObjectType: state.editView.editedObjectType,
  objectIdentifier: editedObjectIdentifierSelector(state.editView, ownProps.subMenuRoute),
  filterOptions: state.filterOptions,
  translationStatusFilterOptions: state.translationStatusFilterOptions
});

const mapDispatchToProps = (dispatch: Dispatch<PdApiActions | EditActions>) => ({
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch),
  editActions: bindActionCreators({ ...editActions }, dispatch),
  mainActions: bindActionCreators({ ...mainActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Localization);

